import { useEffect, useState, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApiQuery } from '../../hooks/useApiQuery';
import { usePlanUsage } from '../../hooks/usePlanUsage';
import { useAlertNotifications } from '../../hooks/useAlertNotifications';
import { AccessDeniedAlert } from '../auth';
import { AlertNotificationStack } from '../alerts';
import AIChatWidget from '../AIChatWidget';
import { useAppTheme } from '../../contexts';

const SIDEBAR_WIDTH = 244;

interface ServerCountResponse {
  currentCount: number;
  maxServers: number;
}

// ─── Inline SVG icons ────────────────────────────────────────────────────────

const IconDashboard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);

const IconServers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/>
    <line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>
  </svg>
);

const IconAlerts = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const IconCloud = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
  </svg>
);

const IconGitHub = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
  </svg>
);


const IconUsers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const IconBilling = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const IconSettings = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const IconAgent = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
  </svg>
);

const IconDocs = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
  </svg>
);

const IconLogout = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconMenu = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

// ─── Navigation structure ────────────────────────────────────────────────────

type NavItem = {
  label: string;
  path: string;
  icon: ReactNode;
  badge?: number;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Monitor',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: <IconDashboard /> },
      { label: 'Servers', path: '/dashboard/servers', icon: <IconServers /> },
      { label: 'Alerts', path: '/dashboard/alerts', icon: <IconAlerts /> },
      { label: 'Agent', path: '/dashboard/agent', icon: <IconAgent /> },
    ],
  },
  {
    title: 'Integrations',
    items: [
      { label: 'AWS', path: '/dashboard/aws', icon: <IconCloud /> },
      { label: 'GitHub', path: '/dashboard/github', icon: <IconGitHub /> },
    ],
  },
  {
    title: 'Account',
    items: [
      { label: 'Users', path: '/dashboard/users', icon: <IconUsers /> },
      { label: 'Billing', path: '/dashboard/billing', icon: <IconBilling /> },
      { label: 'Docs', path: '/dashboard/docs', icon: <IconDocs /> },
      { label: 'Settings', path: '/dashboard/settings', icon: <IconSettings /> },
    ],
  },
];

// ─── Page metadata ───────────────────────────────────────────────────────────

const PAGE_META: Record<string, { title: string; sub: string }> = {
  '/dashboard': { title: 'Dashboard', sub: 'Platform overview' },
  '/dashboard/servers': { title: 'Servers', sub: 'Infrastructure monitoring' },
  '/dashboard/alerts': { title: 'Alerts', sub: 'Alert rules & history' },
  '/dashboard/agent': { title: 'Agent', sub: 'Agent installation & API keys' },
  '/dashboard/aws': { title: 'AWS', sub: 'Cloud integration' },
  '/dashboard/github': { title: 'GitHub', sub: 'Repository & deployment tracking' },
  '/dashboard/ai': { title: 'AI Assistant', sub: 'Conversational infrastructure assistant' },
  '/dashboard/billing': { title: 'Billing', sub: 'Plan, usage & invoices' },
  '/dashboard/docs': { title: 'Docs', sub: 'Feature documentation' },
  '/dashboard/users': { title: 'Users', sub: 'Team members & access management' },
  '/dashboard/settings': { title: 'Settings', sub: 'Company configuration' },
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function CompanyLayout() {
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const avatarBtnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const { data: serverCount } = useApiQuery<ServerCountResponse>(
    ['serverCount', user?.companyId],
    `/companies/${user?.companyId}/servers/count`,
    { enabled: !!user?.companyId },
  );

  const { planUsage } = usePlanUsage();
  const { theme, density: _density, setTheme, setDensity: _setDensity } = useAppTheme();

  // Real-time alert notifications via WebSocket
  const { notifications, dismiss } = useAlertNotifications();

  // API-based active alert count (triggered + acknowledged — persists across page loads)
  const [activeAlertCount, setActiveAlertCount] = useState(0);
  useEffect(() => {
    if (!user?.companyId) return;
    const fetchAlertCount = async () => {
      try {
        const { getAccessToken } = await import('../../services/api');
        const token = getAccessToken();
        if (!token) return;
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
        const res = await fetch(`${baseUrl}/companies/${user.companyId}/alerts/history?page=1&pageSize=50`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const r = await res.json();
          if (r.success && r.data) {
            const count = r.data.filter((a: { status: string }) => a.status === 'triggered' || a.status === 'acknowledged').length;
            setActiveAlertCount(count);
          }
        }
      } catch { /* ignore */ }
    };
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 30_000);
    return () => clearInterval(interval);
  }, [user?.companyId]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('kavrenix.sidebar.collapsed') === 'true');
  const toggleSidebar = () => setSidebarCollapsed(c => { const next = !c; localStorage.setItem('kavrenix.sidebar.collapsed', String(next)); return next; });
  const SIDEBAR_COLLAPSED_WIDTH = 56;
  const [buildVersion, setBuildVersion] = useState<number | null>(null);

  useEffect(() => {
    fetch('/version.json').then(r => r.json()).then(d => setBuildVersion(d.build)).catch(() => {});
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Derive page meta from current path
  const currentPath = location.pathname;
  const meta = PAGE_META[currentPath] || (Object.entries(PAGE_META).find(([path]) => path !== '/dashboard' && currentPath.startsWith(path))?.[1]
    ?? { title: 'Dashboard', sub: 'Server monitoring platform' });

  const userInitial = user?.firstName?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U';
  const userName = user?.firstName
    ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`
    : user?.email || 'User';

  // Alert badge count (max of WebSocket live + API-persisted)
  const wsAlertCount = notifications.filter((n) => n.open && n.type === 'triggered').length;
  const alertBadge = Math.max(wsAlertCount, activeAlertCount);

  // Inject badge into nav items and filter by permissions
  const userPermissions = (user as any)?.permissions as string[] | undefined;
  const isCompanyUser = user?.role === 'company_user';

  const navGroupsWithBadges = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => {
        // Admins and viewers see everything
        if (!isCompanyUser || !userPermissions) return true;
        // Dashboard and Settings are always visible
        if (item.path === '/dashboard' || item.path === '/dashboard/settings') return true;
        // Map path to permission key
        const permKey = item.path.replace('/dashboard/', '');
        return userPermissions.includes(permKey);
      })
      .map((item) => {
        if (item.path === '/dashboard/alerts' && alertBadge > 0) {
          return { ...item, badge: alertBadge };
        }
        return item;
      }),
  })).filter(group => group.items.length > 0);

  // ─── Sidebar content ────────────────────────────────────────────────────


  const sidebarContent = (
    <aside
      style={{
        width: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        height: '100%',
        flexShrink: 0,
        borderRight: '1px solid var(--line)',
        background: 'var(--bg-0)',
        display: 'flex',
        flexDirection: 'column',
        padding: '0',
        position: 'relative',
        overflow: 'hidden',
        transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      {/* Subtle top glow */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 180,
        background: 'radial-gradient(ellipse 100% 60% at 50% 0%, oklch(74% 0.155 270 / .06), transparent)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '14px 12px', position: 'relative', zIndex: 1, overflow: 'hidden' }}>

        {/* ── Brand / Workspace ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: sidebarCollapsed ? 0 : 10,
          padding: sidebarCollapsed ? '9px 0' : '9px 10px', marginBottom: 16,
          borderRadius: 11,
          border: '1px solid var(--line)',
          background: 'var(--bg-2)',
          boxShadow: 'var(--shadow-1)',
          justifyContent: sidebarCollapsed ? 'center' : undefined,
          flexShrink: 0,
        }}>
          {/* Logo mark */}
          <img src="/logo.png" alt="Kavrenix" style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, objectFit: 'contain' }} />

          {!sidebarCollapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '-.02em', color: 'var(--fg-0)' }}>Kavrenix</div>
              <div style={{ fontSize: 10.5, color: 'var(--fg-3)', marginTop: 1 }}>
                {serverCount
                  ? serverCount.maxServers === -1 ? 'Unlimited plan' : `${serverCount.maxServers}-server plan`
                  : '…'}
              </div>
            </div>
          )}

          {/* Live dot */}
          {!sidebarCollapsed && (
            <span style={{
              width: 6, height: 6, borderRadius: 99, flexShrink: 0,
              background: 'var(--ok)',
              boxShadow: '0 0 5px var(--ok)',
              animation: 'sbPulse 2.5s ease-in-out infinite',
            }} />
          )}
        </div>

        {/* ── Scrollable nav area ── */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', marginBottom: 8, scrollbarWidth: 'thin', scrollbarColor: 'var(--bg-3) transparent' }}>
          {navGroupsWithBadges.map((group) => (
            <NavGroupSection
              key={group.title}
              title={group.title}
              items={group.items}
              currentPath={currentPath}
              onNavigate={isMobile ? handleDrawerToggle : undefined}
              collapsed={sidebarCollapsed}
            />
          ))}
        </div>

        {/* ── Plan usage (compact) ── */}
        {!sidebarCollapsed && <div style={{
          padding: '8px 12px', borderRadius: 8,
          background: 'var(--bg-2)',
          border: '1px solid var(--line)',
          marginBottom: 8,
          flexShrink: 0,
          fontSize: 10.5,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '.05em', fontSize: 9.5 }}>
              {planUsage?.planName ?? 'Plan'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, color: 'var(--fg-3)' }}>
            {([
              { label: 'Srv', used: planUsage?.usage.servers ?? serverCount?.currentCount ?? 0, max: planUsage?.limits.maxServers ?? serverCount?.maxServers ?? 0 },
              { label: 'Usr', used: planUsage?.usage.users ?? 0, max: planUsage?.limits.maxUsers ?? 0 },
              { label: 'GH', used: planUsage?.usage.githubTokens ?? 0, max: planUsage?.limits.maxGithubTokens ?? 0 },
              { label: 'AWS', used: planUsage?.usage.awsAccounts ?? 0, max: planUsage?.limits.maxAwsAccounts ?? 0 },
            ] as { label: string; used: number; max: number }[]).map(({ label, used, max }) => (
              <span key={label} style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>
                {label} <strong style={{ color: 'var(--fg-1)' }}>{used}</strong>/{max === -1 ? '∞' : max}
              </span>
            ))}
          </div>
        </div>}

        {/* ── Theme toggle (compact) ── */}
        {!sidebarCollapsed && <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexShrink: 0 }}>
          {(['dark', 'light'] as const).map(t => (
            <button key={t} type="button" onClick={() => setTheme(t)} style={{
              all: 'unset', flex: 1, cursor: 'pointer', textAlign: 'center',
              padding: '4px', borderRadius: 6, fontSize: 10, fontWeight: 600,
              border: theme === t ? '1px solid var(--accent-line)' : '1px solid var(--line)',
              background: theme === t ? 'var(--accent-soft)' : 'var(--bg-2)',
              color: theme === t ? 'var(--accent)' : 'var(--fg-4)',
            }}>
              {t === 'dark' ? '◑' : '○'} {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>}

        {/* ── Build version ── */}
        {!sidebarCollapsed && buildVersion && (
          <div style={{ fontSize: 10, color: 'var(--fg-4)', fontFamily: 'var(--font-mono)', textAlign: 'center', marginBottom: 6 }}>
            build v{buildVersion}
          </div>
        )}

        {/* ── User section ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: sidebarCollapsed ? 0 : 10,
          padding: sidebarCollapsed ? '9px 0' : '9px 10px', borderRadius: 10,
          border: '1px solid transparent',
          transition: 'background .15s, border-color .15s',
          cursor: 'default',
          justifyContent: sidebarCollapsed ? 'center' : undefined,
          flexShrink: 0,
        }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-2)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--line)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.borderColor = 'transparent'; }}
        >
          {/* Avatar */}
          <div style={{
            width: 30, height: 30, borderRadius: 99, flexShrink: 0,
            background: 'linear-gradient(135deg, oklch(62% 0.155 270), oklch(50% 0.14 255))',
            fontSize: 12.5, fontWeight: 700, color: 'oklch(97% 0.01 270)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px oklch(62% 0.155 270 / .3)',
          }}>
            {userInitial}
          </div>

          {!sidebarCollapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg-0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
              <div style={{ fontSize: 10.5, color: 'var(--fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
            </div>
          )}

          {!sidebarCollapsed && (
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Logout"
              style={{
                all: 'unset', cursor: 'pointer',
                width: 26, height: 26, borderRadius: 7,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--fg-4)',
                transition: 'background .15s, color .15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'oklch(65% 0.22 22 / .1)'; (e.currentTarget as HTMLElement).style.color = 'var(--crit)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--fg-4)'; }}
            >
              <IconLogout />
            </button>
          )}
        </div>
      </div>

      {/* Keyframes injected once */}
      <style>{`@keyframes sbPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.75)}}`}</style>
    </aside>
  );

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg-0)' }}>
      {/* Mobile overlay backdrop */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'oklch(6% 0.005 265 / .65)',
            backdropFilter: 'blur(4px)',
            zIndex: 1099,
          }}
        />
      )}

      {/* Sidebar — always rendered, slides in on mobile */}
      <div style={{
        position: isMobile ? 'fixed' : 'relative',
        left: 0, top: 0, bottom: 0,
        height: isMobile ? '100vh' : undefined,
        transform: isMobile && !mobileOpen ? 'translateX(-100%)' : 'none',
        transition: isMobile ? 'transform 0.25s cubic-bezier(0.4,0,0.2,1)' : 'transform 0.25s cubic-bezier(0.4,0,0.2,1), width 0.22s cubic-bezier(0.4,0,0.2,1)',
        zIndex: isMobile ? 1100 : undefined,
        flexShrink: 0,
        width: isMobile ? undefined : (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH),
      }}>
        {sidebarContent}
        {/* Collapse toggle tab — fixed so it escapes overflow:hidden on all ancestors */}
        {!isMobile && (
          <button
            type="button"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              position: 'fixed',
              left: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH - 1 : SIDEBAR_WIDTH - 1,
              top: '50%', transform: 'translateY(-50%)',
              zIndex: 200,
              width: 20, height: 48, borderRadius: '0 8px 8px 0',
              border: '1px solid var(--line)', borderLeft: 'none',
              background: 'var(--bg-1)',
              color: 'var(--fg-3)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '2px 0 8px oklch(0% 0 0 / .2)',
              transition: 'left 0.22s cubic-bezier(0.4,0,0.2,1), background .15s, color .15s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-2)'; (e.currentTarget as HTMLElement).style.color = 'var(--fg-0)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-1)'; (e.currentTarget as HTMLElement).style.color = 'var(--fg-3)'; }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.22s' }}>
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
        )}
      </div>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Topbar */}
        <header
          style={{
            height: 56,
            padding: '0 24px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            background: 'var(--bg-1)',
            backdropFilter: 'blur(10px)',
            position: 'sticky',
            top: 0,
            zIndex: 4,
            flexShrink: 0,
          }}
        >
          {isMobile && (
            <button
              type="button"
              aria-label="open sidebar"
              onClick={handleDrawerToggle}
              style={{
                all: 'unset', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8,
                color: 'var(--fg-2)',
                transition: 'background .15s',
                marginRight: 4,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-2)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <IconMenu />
            </button>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{meta.title}</span>
            {meta.sub && <span style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>{meta.sub}</span>}
          </div>

          <span style={{ flex: 1 }} />

          {/* Security notifications bell */}
          <SecurityNotificationBell />

          {/* User avatar with dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              ref={avatarBtnRef}
              type="button"
              onClick={() => {
                if (!profileMenuOpen && avatarBtnRef.current) {
                  const r = avatarBtnRef.current.getBoundingClientRect();
                  setMenuPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
                }
                setProfileMenuOpen(o => !o);
              }}
              style={{
                all: 'unset',
                cursor: 'pointer',
                width: 32,
                height: 32,
                borderRadius: 99,
                background: 'linear-gradient(135deg, oklch(62% 0.12 270), oklch(48% 0.12 250))',
                fontSize: 12.5,
                fontWeight: 700,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 0 2px var(--accent-line)',
              }}
            >
              {userInitial}
            </button>

            {profileMenuOpen && createPortal(
              <>
                {/* backdrop — renders into body, no stacking context issues */}
                <div onClick={() => setProfileMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />

                {/* dropdown — portalled to body, always on top */}
                <div style={{
                  position: 'fixed',
                  top: menuPos.top,
                  right: menuPos.right,
                  zIndex: 9999,
                  minWidth: 220,
                  background: 'var(--bg-1)',
                  border: '1px solid var(--line-strong)',
                  borderRadius: 12,
                  boxShadow: 'var(--shadow-pop)',
                  overflow: 'hidden',
                  animation: 'slideDown .15s ease',
                }}>
                  {/* user info header */}
                  <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--line-soft)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 99, flexShrink: 0,
                        background: 'linear-gradient(135deg, oklch(62% 0.12 270), oklch(48% 0.12 250))',
                        fontSize: 13, fontWeight: 700, color: '#fff',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}>{userInitial}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
                        <div style={{ fontSize: 11, color: 'var(--fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
                      </div>
                    </div>
                  </div>

                  {/* menu items */}
                  <div style={{ padding: '4px 0' }}>
                    {[
                      { icon: <IconSettings />, label: 'Settings', onClick: () => { setProfileMenuOpen(false); navigate('/dashboard/settings'); } },
                      { icon: <IconSearch />, label: 'Support', onClick: () => { setProfileMenuOpen(false); window.open('mailto:support@kavrenix.com', '_blank'); } },
                    ].map(item => (
                      <button key={item.label} type="button" onClick={item.onClick}
                        style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 14px', fontSize: 13, color: 'var(--fg-1)', boxSizing: 'border-box', transition: 'background .1s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-2)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <span style={{ color: 'var(--fg-3)', display: 'inline-flex' }}>{item.icon}</span>
                        {item.label}
                      </button>
                    ))}
                  </div>

                  <div style={{ height: 1, background: 'var(--line-soft)' }} />

                  <div style={{ padding: '4px 0' }}>
                    <button type="button"
                      onClick={() => { setProfileMenuOpen(false); handleLogout(); }}
                      style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 14px', fontSize: 13, color: 'var(--crit)', boxSizing: 'border-box', transition: 'background .1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--crit-soft)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <IconLogout /> Sign out
                    </button>
                  </div>
                </div>
              </>,
              document.body,
            )}
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: 'var(--pad)', background: 'var(--bg-0)', display: 'flex', flexDirection: 'column' }}>
          <AccessDeniedAlert />
          <Outlet />
        </main>
      </div>

      {/* Real-time alert notifications */}
      <AlertNotificationStack notifications={notifications} onDismiss={dismiss} />

      {/* Floating AI chat widget */}
      <AIChatWidget />

    </div>
  );
}

// ─── NavGroupSection ─────────────────────────────────────────────────────────

function NavGroupSection({
  title,
  items,
  currentPath,
  onNavigate,
  collapsed,
}: {
  title: string;
  items: NavItem[];
  currentPath: string;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 14 }}>
      {!collapsed && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: 'var(--fg-3)',
            padding: '4px 10px 8px',
          }}
        >
          {title}
        </div>
      )}
      {items.map((item) => (
        <SidebarNavItem
          key={item.path}
          item={item}
          active={item.path === '/dashboard' ? currentPath === '/dashboard' : currentPath.startsWith(item.path)}
          onNavigate={onNavigate}
          collapsed={collapsed}
        />
      ))}
    </div>
  );
}

// ─── SidebarNavItem ──────────────────────────────────────────────────────────

function SidebarNavItem({
  item,
  active,
  onNavigate,
  collapsed,
}: {
  item: NavItem;
  active: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: collapsed ? 0 : 10,
        padding: collapsed ? '7px 0' : '7px 10px 7px 12px',
        borderRadius: 8,
        color: active ? 'var(--fg-0)' : 'var(--fg-1)',
        background: active ? 'var(--bg-2)' : 'transparent',
        boxShadow: active ? 'inset 0 0 0 1px var(--line)' : 'none',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        position: 'relative',
        transition: 'background .15s ease, color .15s ease',
        borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
        justifyContent: collapsed ? 'center' : undefined,
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--bg-1)';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <span style={{ color: active ? 'var(--accent)' : 'var(--fg-2)', display: 'inline-flex' }}>
        {item.icon}
      </span>
      {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
      {!collapsed && item.badge != null && item.badge > 0 && (
        <span
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: 'var(--fg-2)',
            background: 'var(--bg-3)',
            padding: '1px 6px',
            borderRadius: 99,
            minWidth: 18,
            textAlign: 'center',
          }}
        >
          {item.badge}
        </span>
      )}
    </Link>
  );
}


// ─── Security Notification Bell ──────────────────────────────────────────────

function SecurityNotificationBell() {
  const [notifications, setNotifications] = useState<{ id: string; message: string; type: string }[]>([]);
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.companyId) return;
    const checkSecurity = async () => {
      try {
        const { getAccessToken } = await import('../../services/api');
        const token = getAccessToken();
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

        // Check API key age
        const res = await fetch(`${baseUrl}/companies/${payload.companyId}/api-key`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const r = await res.json();
          if (r.data?.createdAt) {
            const daysSince = Math.floor((Date.now() - new Date(r.data.createdAt).getTime()) / 86400000);
            if (daysSince >= 90) {
              setNotifications(prev => {
                if (prev.find(n => n.id === 'key-rotation')) return prev;
                return [...prev, { id: 'key-rotation', message: `API key is ${daysSince} days old. Rotate it for better security.`, type: 'warning' }];
              });
            }
          }
        }

        // Check password age (using profile updatedAt as proxy)
        const profileRes = await fetch(`${baseUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (profileRes.ok) {
          const pr = await profileRes.json();
          if (pr.data?.updatedAt) {
            const pwDays = Math.floor((Date.now() - new Date(pr.data.updatedAt).getTime()) / 86400000);
            if (pwDays >= 90) {
              setNotifications(prev => {
                if (prev.find(n => n.id === 'password-age')) return prev;
                return [...prev, { id: 'password-age', message: `Password hasn't been changed in ${pwDays} days. Reset it for best security practices.`, type: 'warning' }];
              });
            }
          }
        }

        // Check agent version mismatch
        const [versionRes, serversRes] = await Promise.all([
          fetch(`${baseUrl}/agent/version`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${baseUrl}/companies/${payload.companyId}/servers`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        let serversList: any[] = [];
        if (versionRes.ok && serversRes.ok) {
          const vr = await versionRes.json();
          const sr = await serversRes.json();
          const latest = vr.data?.latestVersion;
          if (sr.success && sr.data) serversList = sr.data;
          if (latest && serversList.length > 0) {
            const outdated = serversList.filter((s: any) => s.agentVersion && s.agentVersion !== latest);
            if (outdated.length > 0) {
              setNotifications(prev => {
                if (prev.find(n => n.id === 'agent-mismatch')) return prev;
                return [...prev, { id: 'agent-mismatch', message: `${outdated.length} server${outdated.length > 1 ? 's' : ''} running outdated agent (latest: v${latest}). Upgrade for new features.`, type: 'info' }];
              });
            }
          }
        }

        // Check active alerts
        const alertRes = await fetch(`${baseUrl}/companies/${payload.companyId}/alerts/history?page=1&pageSize=10`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alertRes.ok) {
          const ar = await alertRes.json();
          if (ar.success && ar.data) {
            const active = ar.data.filter((a: any) => a.status === 'triggered' || a.status === 'acknowledged');
            if (active.length > 0) {
              setNotifications(prev => {
                if (prev.find(n => n.id === 'active-alerts')) return prev;
                return [...prev, { id: 'active-alerts', message: `${active.length} active alert${active.length > 1 ? 's' : ''} need attention. Check Alerts page.`, type: 'critical' }];
              });
            }
          }
        }

        // Check database blocking
        for (const server of serversList.slice(0, 5)) {
          try {
            const blockRes = await fetch(`${baseUrl}/companies/${payload.companyId}/servers/${server.id}/blocking-history?limit=5`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (blockRes.ok) {
              const br = await blockRes.json();
              if (br.success && br.data && br.data.length > 0) {
                const recent = br.data.filter((b: any) => Date.now() - new Date(b.collected_at).getTime() < 15 * 60 * 1000);
                if (recent.length > 0) {
                  setNotifications(prev => {
                    if (prev.find(n => n.id === 'db-blocking')) return prev;
                    return [...prev, { id: 'db-blocking', message: `Database blocking detected on ${server.hostname || 'a server'}. ${recent.length} blocked quer${recent.length > 1 ? 'ies' : 'y'}.`, type: 'critical' }];
                  });
                  break; // Only show one blocking notification
                }
              }
            }
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    };
    checkSecurity();
  }, [user?.companyId]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const hasNotifications = notifications.length > 0;

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={bellRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          all: 'unset', cursor: 'pointer', position: 'relative',
          width: 32, height: 32, borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: hasNotifications ? 'oklch(78% 0.16 55)' : 'var(--fg-3)',
          transition: 'background .15s',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {hasNotifications && (
          <span style={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, borderRadius: 99, background: 'oklch(78% 0.16 55)', border: '2px solid var(--bg-0)' }} />
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8,
          width: 300, background: 'var(--bg-1)', border: '1px solid var(--line)',
          borderRadius: 12, boxShadow: '0 16px 48px oklch(0% 0 0 / .3)',
          zIndex: 500, overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', fontSize: 12, fontWeight: 700, color: 'var(--fg-0)' }}>
            Notifications
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 12, color: 'var(--fg-3)' }}>
              All good — no security recommendations.
            </div>
          ) : (
            <div style={{ padding: '8px' }}>
              {notifications.map(n => (
                <div key={n.id} style={{
                  padding: '10px 12px', borderRadius: 8, marginBottom: 4,
                  background: n.type === 'critical' ? 'oklch(65% 0.2 25 / .08)' : n.type === 'info' ? 'oklch(70% 0.15 250 / .08)' : 'oklch(78% 0.16 55 / .06)',
                  border: `1px solid ${n.type === 'critical' ? 'oklch(65% 0.2 25 / .2)' : n.type === 'info' ? 'oklch(70% 0.15 250 / .2)' : 'oklch(78% 0.16 55 / .15)'}`,
                  fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.5,
                }}>
                  <span style={{ marginRight: 6 }}>{n.type === 'critical' ? '🚨' : n.type === 'info' ? 'ℹ️' : '⚠️'}</span>{n.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
